import { useEffect } from "react";
import { isNetworkError } from "../helpers/networkErrors";
import { useOfflineDialog } from "../context/OfflineContext";

/**
 * Global error handler hook that catches unhandled promise rejections
 * and shows the offline dialog for network errors.
 *
 * This provides a safety net so you don't need to wrap every single
 * API call with try-catch for offline handling.
 */
const useGlobalErrorHandler = () => {
  const { showOfflineDialog } = useOfflineDialog();

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const error = event.reason;

      // Check if this is a network/offline error
      if (isNetworkError(error)) {
        showOfflineDialog();
        // Prevent the error from being logged to console as unhandled
        event.preventDefault();
      }
    };

    const handleError = (event) => {
      const error = event.error;

      // Check if this is a network/offline error
      if (error && isNetworkError(error)) {
        showOfflineDialog();
        event.preventDefault();
      }
    };

    // Listen for unhandled promise rejections
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    // Listen for uncaught errors
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
      window.removeEventListener("error", handleError);
    };
  }, [showOfflineDialog]);
};

export default useGlobalErrorHandler;
